import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { AccrualReviewApproveResponse } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { MatchableItem, MatchableTransaction } from "./types";
import {
    addItemToSelection,
    createSelectionState,
    enrichSelectionState,
    removeItemFromSelection,
} from "./matchSelection";
import { AccrualMatchCtx } from "./accrualMatch.context";
import { AccrualMatchViewProvider } from "./AccrualMatchViewProvider";

interface ProviderProps {
    sourceTransaction: MatchableTransaction | null;
    initialSelectedItems?: MatchableItem[];
    /**
     * Ids of the seeded items that are actually persisted on the server
     * (existing AccrualMatch documents plus any unconfirmed-but-persisted
     * transfer partner). Distinct from `initialSelectedItems`, which can also
     * carry unsaved accrual-review suggestions. Defaults to every seeded id
     * when omitted (callers whose whole seed is persisted, e.g. the reverse
     * drawers).
     */
    persistedItemIds?: string[];
    currentTxLineItemMatchAmounts?: ReadonlyMap<string, number>;
    ledgerBasis?: AccountingLedgerBasis;
    isGlFirst?: boolean;
    children: React.ReactNode;
    onOpenEditAllocation?: (sourceItem: MatchableItem) => void;
    onConfirmSuccess?: (response: AccrualReviewApproveResponse) => void;
    onClose?: () => void;
}

export const AccrualMatchProvider: React.FC<ProviderProps> = ({
    sourceTransaction,
    initialSelectedItems,
    persistedItemIds,
    currentTxLineItemMatchAmounts,
    ledgerBasis = AccountingLedgerBasis.accruals,
    isGlFirst = true,
    children,
    onOpenEditAllocation,
    onConfirmSuccess,
    onClose,
}) => {
    const isCashBasis = ledgerBasis === AccountingLedgerBasis.cash;
    const [selectionState, setSelectionState] = useState(() =>
        createSelectionState({
            items: initialSelectedItems ?? [],
            ledgerBasis,
        }),
    );
    const { selectedItems } = selectionState;

    // Starts as the drawer's persisted seed (existing AccrualMatch documents
    // plus any unconfirmed-but-persisted transfer partner). Unsaved
    // accrual-review suggestions must be excluded — they arrive in
    // `initialSelectedItems` but are not persisted, so counting them here
    // would disable confirmation for a match that was never saved. Callers
    // that don't pass `persistedItemIds` seed their whole selection as
    // persisted. Enrichment queries resolving replace items in place via
    // `enrichSelectionState` without changing which ids are persisted; a match
    // the details refetch reports only after mount (the drawer opened on a
    // stale cache) is adopted during render below, so a reopened drawer shows
    // what the server already saved instead of offering it as a fresh
    // selection.
    const [persistedItemIdSet, setPersistedItemIdSet] = useState<
        ReadonlySet<string>
    >(
        () =>
            new Set(
                persistedItemIds ??
                    (initialSelectedItems ?? []).map((item) => item.id),
            ),
    );
    const [adoptedPersistedItemIds, setAdoptedPersistedItemIds] =
        useState(persistedItemIds);
    if (persistedItemIds !== adoptedPersistedItemIds) {
        setAdoptedPersistedItemIds(persistedItemIds);
        const itemsById = new Map(
            (initialSelectedItems ?? []).map((item) => [item.id, item]),
        );
        const adoptedIds = (persistedItemIds ?? []).filter(
            (id) => itemsById.has(id) && !persistedItemIdSet.has(id),
        );
        if (adoptedIds.length > 0) {
            setPersistedItemIdSet(
                new Set([...persistedItemIdSet, ...adoptedIds]),
            );
            // An item the user already selected (or a seeded suggestion) only
            // becomes persisted; appending it again would double the row.
            const presentIds = new Set(
                [
                    ...selectionState.selectedItems,
                    ...selectionState.displacedTransferItems,
                ].map((item) => item.id),
            );
            const addedItems = adoptedIds.flatMap((id) => {
                const item = itemsById.get(id);
                return item && !presentIds.has(id) ? [item] : [];
            });
            // Same partitioning as the seed, so a cash-basis transfer pair
            // arriving next to documents is set aside rather than replacing
            // them.
            if (addedItems.length > 0) {
                setSelectionState(
                    createSelectionState({
                        items: [
                            ...selectionState.selectedItems,
                            ...selectionState.displacedTransferItems,
                            ...addedItems,
                        ],
                        ledgerBasis,
                    }),
                );
            }
        }
    }

    // `ledgerBasis` is not stable for the life of the drawer: the ledger query
    // fails open to "accruals" while it is in flight, so the seeded state (and
    // anything added before it resolves) can hold a transfer pair and
    // documents side by side — a combination the cash-basis approve rejects.
    // Re-partition once the real basis arrives.
    const prevLedgerBasisRef = useRef(ledgerBasis);
    useEffect(() => {
        if (prevLedgerBasisRef.current === ledgerBasis) {
            return;
        }
        prevLedgerBasisRef.current = ledgerBasis;
        setSelectionState((prev) =>
            createSelectionState({
                items: [...prev.selectedItems, ...prev.displacedTransferItems],
                ledgerBasis,
            }),
        );
    }, [ledgerBasis]);

    const prevInitialRef = useRef(initialSelectedItems);
    useEffect(() => {
        if (
            initialSelectedItems &&
            initialSelectedItems !== prevInitialRef.current
        ) {
            prevInitialRef.current = initialSelectedItems;
            const enrichedMap = new Map(
                initialSelectedItems.map((i) => [i.id, i]),
            );
            setSelectionState((prev) =>
                enrichSelectionState({ state: prev, itemsById: enrichedMap }),
            );
        }
    }, [initialSelectedItems]);

    const addItem = useCallback(
        (item: MatchableItem) => {
            setSelectionState((prev) =>
                addItemToSelection({ state: prev, item, ledgerBasis }),
            );
        },
        [ledgerBasis],
    );

    const removeItem = useCallback((id: string) => {
        setSelectionState((prev) =>
            removeItemFromSelection({ state: prev, itemId: id }),
        );
    }, []);

    const selectedItemIds = useMemo(
        () => new Set(selectedItems.map((i) => i.id)),
        [selectedItems],
    );

    const effectiveCurrentTxMatches = useMemo(
        () => currentTxLineItemMatchAmounts ?? new Map<string, number>(),
        [currentTxLineItemMatchAmounts],
    );

    const value = useMemo(
        () => ({
            sourceTransaction,
            selectedItems,
            addItem,
            removeItem,
            selectedItemIds,
            persistedItemIds: persistedItemIdSet,
            currentTxLineItemMatchAmounts: effectiveCurrentTxMatches,
            ledgerBasis,
            isCashBasis,
            isGlFirst,
            onOpenEditAllocation,
            onConfirmSuccess,
            onClose,
        }),
        [
            sourceTransaction,
            selectedItems,
            addItem,
            removeItem,
            selectedItemIds,
            persistedItemIdSet,
            effectiveCurrentTxMatches,
            ledgerBasis,
            isCashBasis,
            isGlFirst,
            onOpenEditAllocation,
            onConfirmSuccess,
            onClose,
        ],
    );

    // The view ctx is in its own provider so frequent tableView updates
    // (e.g. during a column resize drag) only re-render AccrualMatchTable
    // instances, not the page / footer / search / drawer components that
    // consume AccrualMatchCtx.
    return (
        <AccrualMatchCtx.Provider value={value}>
            <AccrualMatchViewProvider columnsVariant="workspaceMatched">
                {children}
            </AccrualMatchViewProvider>
        </AccrualMatchCtx.Provider>
    );
};
